import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { todoApi } from '../api/todoApi';
import { useUser } from '../contexts/UserContext';
import { TodoList } from '../types/todo';

interface ListManagementData {
  name: string;
  description?: string;
  color?: string;
  isShared?: boolean;
  sharedWith?: string[];
  isPublic?: boolean;
}

interface UseListManagementProps {
  onListCreated?: (newList: TodoList) => void;
  onListUpdated?: () => void;
  onListDeleted?: () => void;
}

export const useListManagement = ({ 
  onListCreated, 
  onListUpdated, 
  onListDeleted 
}: UseListManagementProps = {}) => {
  const { currentUser } = useUser();
  const queryClient = useQueryClient();
  
  // Modal states
  const [showCreateListForm, setShowCreateListForm] = useState(false);
  const [showEditListForm, setShowEditListForm] = useState(false);
  const [showListInfoDialog, setShowListInfoDialog] = useState(false);
  const [newPublicUrl, setNewPublicUrl] = useState<string | null>(null);
  
  // Form data states
  const [createListData, setCreateListData] = useState<{ name: string; description?: string; color?: string } | null>(null);
  const [editListData, setEditListData] = useState<ListManagementData | null>(null);
  
  // Selected list for operations
  const [selectedList, setSelectedList] = useState<TodoList | null>(null);

  // Create list mutation
  const createListMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; color?: string }) => 
      todoApi.createList(data, currentUser?.email || ''),
    onSuccess: (newList) => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowCreateListForm(false);
      setCreateListData(null);
      
      if (newList && onListCreated) {
        onListCreated(newList);
      }
    },
    onError: (error) => {
      console.error('List creation failed:', error);
    },
  });

  // Update list mutation with celebration modal
  const updateListMutation = useMutation({
    mutationFn: async (data: ListManagementData) => {
      if (!selectedList) return null;
      
      // Check if this is the first time making the list public
      const isNewlyPublic = data.isPublic && !selectedList.isPublic;
      
      // First update basic list info
      const updatedList = await todoApi.updateList(selectedList.id, {
        name: data.name,
        description: data.description,
        color: data.color,
        isPublic: data.isPublic
      }, currentUser?.email || '');
      
      // Then update sharing if it changed
      const sharingChanged = data.isShared !== selectedList.isShared || 
        JSON.stringify(data.sharedWith) !== JSON.stringify(selectedList.sharedWith);
        
      if (sharingChanged) {
        await todoApi.updateListSharing(
          selectedList.id, 
          data.isShared || false, 
          data.sharedWith || [], 
          currentUser?.email || ''
        );
      }
      
      return { updatedList, isNewlyPublic };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      
      // Check if we generated a new public URL
      if (result?.isNewlyPublic && result.updatedList?.publicAccessToken) {
        const publicUrl = `${window.location.origin}/public/${result.updatedList.publicAccessToken}`;
        
        // Delay showing celebration modal to ensure edit modal closes first
        setTimeout(() => {
          setNewPublicUrl(publicUrl);
        }, 100);
      }
      
      setShowEditListForm(false);
      setEditListData(null);
      
      if (onListUpdated) {
        onListUpdated();
      }
    },
    onError: (error) => {
      console.error('List update failed:', error);
    },
  });

  // Delete list mutation
  const deleteListMutation = useMutation({
    mutationFn: () => {
      if (!selectedList) return Promise.resolve();
      return todoApi.deleteList(selectedList.id, currentUser?.email || '');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todolists', currentUser?.email] });
      setShowEditListForm(false);
      setEditListData(null);
      
      if (onListDeleted) {
        onListDeleted();
      }
    },
    onError: (error) => {
      console.error('List deletion failed:', error);
    },
  });

  // Action handlers
  const handleEditList = (list: TodoList) => {
    setSelectedList(list);
    if (currentUser?.email === list.createdBy) {
      setShowEditListForm(true);
    } else {
      setShowListInfoDialog(true);
    }
  };

  const handleCreateList = () => {
    setShowCreateListForm(true);
  };

  const handleSaveCreate = () => {
    if (createListData && createListData.name.trim()) {
      createListMutation.mutate(createListData);
    }
  };

  const handleSaveEdit = () => {
    if (editListData && editListData.name.trim()) {
      updateListMutation.mutate(editListData);
    }
  };

  const handleDeleteList = () => {
    deleteListMutation.mutate();
  };

  // Close handlers
  const closeCreateForm = () => {
    setShowCreateListForm(false);
    setCreateListData(null);
  };

  const closeEditForm = () => {
    setShowEditListForm(false);
    setEditListData(null);
    setSelectedList(null);
  };

  const closeListInfo = () => {
    setShowListInfoDialog(false);
    setSelectedList(null);
  };

  const closeCelebrationModal = () => {
    setNewPublicUrl(null);
  };

  return {
    // States
    showCreateListForm,
    showEditListForm,
    showListInfoDialog,
    newPublicUrl,
    createListData,
    editListData,
    selectedList,
    
    // Setters for form data
    setCreateListData,
    setEditListData,
    
    // Mutations
    createListMutation,
    updateListMutation,
    deleteListMutation,
    
    // Action handlers
    handleEditList,
    handleCreateList,
    handleSaveCreate,
    handleSaveEdit,
    handleDeleteList,
    
    // Close handlers
    closeCreateForm,
    closeEditForm,
    closeListInfo,
    closeCelebrationModal,
  };
};